<template lang="html">
	<core-dropdown-button
		ref="statusDropdown"
		class="campaign-table-status-indicators"
		:class="{ 'status-is-scheduled': isScheduled }"
		:link-style="true"
		:width="112"
		:open="statusOpened"
		@close="$emit('close')"
	>
		<core-loading v-if="!$store.getters.isFetched('wpResources')" size="22px" style="margin: 0" />
		<template v-else>
			<span :class="statusColorClass">{{ statusText }}</span>
			<common-tooltip v-show="isScheduled" icon="svg-calendar" :delay-on-mouse-over="100">
				<template #tooltip>
					<campaigns-rules-schedule :rules="scheduledRules" />
				</template>
			</common-tooltip>
		</template>

		<template #list>
			<li>
				<a href="#" :class="{ current: 'Draft' === statusText }" @click.prevent="setDraftStatus"> Draft </a>
			</li>
			<li v-if="!isSplit">
				<a href="#" :class="{ current: 'Pending' === statusText }" @click.prevent="setPendingStatus">
					Pending
				</a>
			</li>
			<li>
				<a
					href="#"
					:class="{ current: 'Published' === statusText && !isScheduled }"
					@click.prevent="setPublishStatus"
				>
					Publish
				</a>
			</li>
			<li>
				<a
					:href="scheduleLink"
					:class="{ current: 'Published' === statusText && isScheduled }"
					@click="maybeEditSchedule"
				>
					{{ isScheduled ? 'Edit Schedule' : 'Set Schedule' }}
				</a>
			</li>
		</template>
	</core-dropdown-button>
</template>
<script lang="js">
import { campaign } from '@/campaigns/mixins/campaign';
import { mapGetters } from 'vuex';

export default {
	mixins: [campaign],
	props: {
		/**
		 * Campaign Id
		 */
		campaignId: {
			type: String,
			default: '',
		},
		/**
		 * Whether dropdown is triggered open.
		 */
		statusOpened: Boolean,
	},
	computed: {
		...mapGetters('campaigns', ['getCampaign']),
		/**
		 * Get the campaign object.
		 *
		 * @returns {object} The campaign object.
		 */
		campaign() {
			return this.getCampaign(this.campaignId);
		},
		statusColorClass() {
			switch (this.statusText) {
				case 'Pending':
					return 'omapi-c-orange';
				case 'Published':
					return 'omapi-c-green';
				default:
				case 'Draft':
					return 'omapi-c-red';
			}
		},
		/**
		 * Get the appropriate text for the campaign status column.
		 *
		 * - Seeing "Draft" status always indicates app status is Paused
		 *   (regardless of embed code status)
		 *
		 * - Seeing "Pending" status always indicates either embed code status
		 *   is disabled, or app status is Paused
		 *
		 * - Seeing "Published" status always indicates app status is Active
		 *   and embed code status is enabled
		 *
		 * @returns {string} The text for the campaign status.
		 */
		statusText() {
			if (this.isSplit) {
				return this.isPublished ? 'Published' : 'Draft';
			}

			if (!this.isPublished) {
				return this.settingEnabled ? 'Pending' : 'Draft';
			}

			return this.settingEnabled ? 'Published' : 'Pending';
		},
	},
	methods: {
		/**
		 * Sets status to "Draft"
		 *
		 * - Toggles app status to "paused"
		 * - Toggles embed code disabled
		 *
		 * @returns {void}
		 */
		setDraftStatus() {
			this.handleStatus('paused');
			this.toggleEmbed(false);
		},
		/**
		 * Sets status to "Pending"
		 *
		 * - Does not touch app status (will be left Paused/Active)
		 * - Toggles embed code disabled if app status is "Active", else enabled.
		 *
		 * @returns {void}
		 */
		setPendingStatus() {
			this.toggleEmbed(!this.isPublished);
		},
		/**
		 * Sets status to "Publish"
		 *
		 * - Toggles app status to "active"
		 * - Toggles embed code enabled
		 *
		 * @returns {void}
		 */
		setPublishStatus() {
			this.handleStatus('active');
			this.toggleEmbed(true);
		},
		/**
		 * Set status to paused/active.
		 *
		 * @since 2.0.0
		 *
		 * @param {string} status The status to set.
		 *
		 * @returns {void}
		 */
		handleStatus(status) {
			this.$refs.statusDropdown.toggle(true);
			this.setStatus({ campaigns: [this.id], status });
		},
		/**
		 * Toggle the embed code on/off.
		 *
		 * @since  2.0.0
		 *
		 * @param {boolean} enabled Whether to set embed enabled/disabled.
		 *
		 * @returns {void}
		 */
		toggleEmbed(enabled) {
			enabled = Boolean(enabled);

			this.$refs.statusDropdown.toggle(true);

			if (enabled === this.settingEnabled) {
				return;
			}

			this.settingEnabled = enabled;

			this.saveWordPress({
				campaignId: this.id,
				settings: this.getSettings(),
			}).then(() => {
				this.fetchWpDataForCampaign({ campaignId: this.id })
					.then(() => this.fetched(this.id))
					.catch(() => {});
			});
		},
	},
};
</script>
